/**
 * Grading Report — cross-property roll-up at /admin/grading-report.
 *
 * One row per active property with all the inputs + computed grade.
 * Sortable by any column, filterable by region/city/status, with a
 * single-click Excel export. Default sort: lowest final grade first
 * so bad accounts surface to the top.
 *
 * Each row's property name is a Link to that property's detail page.
 */
import { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../api';
import { formatDate } from '../../utils/dateUtils';
import PeriodSelector from '../../components/PeriodSelector';

// Numeric weight per grade for sorting "lowest grade first" — F/null sink
// to the bottom (or top, if dir is asc). Keeps the data-driven cycle list
// intuitive.
const GRADE_WEIGHT = {
  'A+': 13, 'A': 12, 'A-': 11,
  'B+': 10, 'B':  9, 'B-':  8,
  'C+':  7, 'C':  6, 'C-':  5,
  'D+':  4, 'D':  3, 'D-':  2,
  'F':   1,
};

const GRADE_COLORS = {
  'A+': '#059669', 'A': '#059669', 'A-': '#059669',
  'B+': '#10b981', 'B': '#10b981', 'B-': '#10b981',
  'C+': '#f59e0b', 'C': '#f59e0b', 'C-': '#f59e0b',
  'D+': '#f97316', 'D': '#f97316', 'D-': '#f97316',
  'F': '#dc2626',
};

const STATUS_STYLES = {
  MAXED_OUT:        { label: 'MAXED OUT',        bg: '#dcfce7', color: '#166534' },
  UNDERPERFORMING:  { label: 'UNDERPERFORMING',  bg: '#fef3c7', color: '#92400e' },
  CRITICAL:         { label: 'CRITICAL',         bg: '#fee2e2', color: '#991b1b' },
  INSUFFICIENT_DATA:{ label: 'INSUFFICIENT DATA',bg: '#f3f4f6', color: '#6b7280' },
};

const KPI_LABELS = {
  profit_margin: 'Profit Margin',
  expense_ratio: 'Expense Ratio',
  true_no_charge_rate: 'True No Charge Rate',
  courtesy_tow_rate: 'Courtesy Tow Rate',
  tow_fee: 'Tow Fee',
};

function GradeChip({ grade, sm }) {
  if (!grade) return <span style={{ color: '#9ca3af' }}>—</span>;
  const color = GRADE_COLORS[grade] || '#6b7280';
  return (
    <span style={{
      display: 'inline-block',
      padding: sm ? '1px 6px' : '3px 10px',
      borderRadius: 4,
      fontSize: sm ? 10 : 12,
      fontWeight: 800,
      backgroundColor: `${color}20`,
      color,
      letterSpacing: 0.4,
    }}>
      {grade}
    </span>
  );
}

function StatusChip({ status }) {
  const s = STATUS_STYLES[status] || { label: status || '—', bg: '#f3f4f6', color: '#6b7280' };
  return (
    <span style={{
      display: 'inline-block',
      padding: '2px 8px',
      borderRadius: 4,
      fontSize: 10,
      fontWeight: 700,
      backgroundColor: s.bg,
      color: s.color,
      letterSpacing: 0.4,
      whiteSpace: 'nowrap',
    }}>
      {s.label}
    </span>
  );
}

function SummaryBar({ summary }) {
  if (!summary) return null;
  const cards = [
    { label: 'Total', value: summary.total, color: '#374151', bg: '#f9fafb' },
    { label: 'Maxed Out', value: summary.maxed_out, color: '#166534', bg: '#dcfce7' },
    { label: 'Underperforming', value: summary.underperforming, color: '#92400e', bg: '#fef3c7' },
    { label: 'Critical', value: summary.critical, color: '#991b1b', bg: '#fee2e2' },
    { label: 'Insufficient Data', value: summary.insufficient_data, color: '#6b7280', bg: '#f3f4f6' },
  ];
  return (
    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
      {cards.map(c => (
        <div key={c.label} style={{
          padding: '10px 16px',
          borderRadius: 8,
          backgroundColor: c.bg,
          minWidth: 130,
          flex: '0 1 auto',
        }}>
          <div style={{
            fontSize: 10, fontWeight: 700, color: c.color,
            textTransform: 'uppercase', letterSpacing: 0.6,
          }}>{c.label}</div>
          <div style={{ fontSize: 24, fontWeight: 700, color: c.color, lineHeight: 1.1 }}>
            {c.value}
          </div>
        </div>
      ))}
    </div>
  );
}

function fmtPct(v) { return v == null || !Number.isFinite(v) ? '—' : `${(v * 100).toFixed(2)}%`; }
function fmtMoney(v) { return v == null || !Number.isFinite(v) ? '—' : `$${Number(v).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}`; }
function fmtCount(v) { return v == null ? '—' : Number(v).toLocaleString(); }

export default function GradingReportPage() {
  const [period, setPeriod] = useState({ kind: 'mtd' });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [exporting, setExporting] = useState(false);

  // Filters
  const [regionFilter, setRegionFilter] = useState('');
  const [cityFilter, setCityFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');

  // Sort — default: lowest final_grade first (i.e. sort by GRADE_WEIGHT asc).
  // Insufficient-data and missing grades sink to the bottom.
  const [sortKey, setSortKey] = useState('final_grade');
  const [sortDir, setSortDir] = useState('asc');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const params = { period: period.kind };
    if (period.kind === 'custom' && period.start && period.end) {
      params.start = period.start.slice(0, 10);
      params.end = period.end.slice(0, 10);
    }
    api.getGradingReport(params)
      .then(d => { if (!cancelled) { setData(d); setError(''); } })
      .catch(e => { if (!cancelled) setError(e.message || 'Failed to load report'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [period]);

  const exportXlsx = async () => {
    setExporting(true);
    try {
      const params = { period: period.kind };
      if (period.kind === 'custom' && period.start && period.end) {
        params.start = period.start.slice(0, 10);
        params.end = period.end.slice(0, 10);
      }
      await api.downloadGradingReport(params);
    } catch (e) {
      alert(e.message);
    } finally { setExporting(false); }
  };

  // Region/city options driven by the dataset itself so admin doesn't have
  // to type them. Always show "All" prefix entry.
  const regionOptions = useMemo(() => {
    const set = new Set((data?.rows || []).map(r => r.region).filter(Boolean));
    return [...set].sort();
  }, [data]);
  const cityOptions = useMemo(() => {
    const set = new Set((data?.rows || []).map(r => r.city).filter(Boolean));
    return [...set].sort();
  }, [data]);

  // Filtered + sorted rows
  const filtered = useMemo(() => {
    if (!data?.rows) return [];
    const q = search.trim().toLowerCase();
    let out = data.rows.filter(r => {
      if (regionFilter && r.region !== regionFilter) return false;
      if (cityFilter && r.city !== cityFilter) return false;
      if (statusFilter && r.status !== statusFilter) return false;
      if (q && !`${r.property_name} ${r.address || ''}`.toLowerCase().includes(q)) return false;
      return true;
    });
    const dir = sortDir === 'asc' ? 1 : -1;
    out.sort((a, b) => {
      let av = a[sortKey], bv = b[sortKey];
      if (sortKey === 'final_grade' || sortKey === 'raw_grade') {
        av = GRADE_WEIGHT[av] ?? 0;
        bv = GRADE_WEIGHT[bv] ?? 0;
      }
      if (av == null && bv == null) return 0;
      if (av == null) return 1;          // nulls last regardless of dir
      if (bv == null) return -1;
      if (typeof av === 'string') return av.localeCompare(bv) * dir;
      return (av - bv) * dir;
    });
    return out;
  }, [data, search, regionFilter, cityFilter, statusFilter, sortKey, sortDir]);

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir(key === 'final_grade' ? 'asc' : 'desc'); }
  };

  const clearFilters = () => {
    setRegionFilter(''); setCityFilter(''); setStatusFilter(''); setSearch('');
  };

  if (loading && !data) {
    return <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>Loading report…</div>;
  }
  if (error && !data) {
    return <div style={{ padding: 40, textAlign: 'center', color: '#ef4444' }}>{error}</div>;
  }

  // Column headers — clickable to sort
  const SortHeader = ({ k, children, align = 'left' }) => (
    <th
      onClick={() => toggleSort(k)}
      style={{ cursor: 'pointer', textAlign: align, whiteSpace: 'nowrap' }}
    >
      {children}
      {sortKey === k && (
        <span style={{ marginLeft: 4, color: '#9ca3af', fontSize: 11 }}>
          {sortDir === 'asc' ? '▲' : '▼'}
        </span>
      )}
    </th>
  );

  return (
    <div style={{ padding: '24px 32px', maxWidth: 1500 }}>
      <div className="page-header" style={{ marginBottom: 16 }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 700, color: '#111827', margin: 0 }}>
            Grading Report
          </h1>
          <p style={{ color: '#6b7280', fontSize: 14, margin: '4px 0 0' }}>
            Cross-property roll-up — sort and filter to find accounts to cycle or focus on
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={exportXlsx} disabled={exporting || !data?.rows?.length}
            className="btn" style={{
              backgroundColor: '#10b981', color: '#fff', fontWeight: 600,
            }}>
            {exporting ? 'Exporting…' : '↓ Export Excel'}
          </button>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap', marginBottom: 16 }}>
        <PeriodSelector value={period} onChange={setPeriod} />
        {data?.period?.start && (
          <span style={{ fontSize: 12, color: '#9ca3af' }}>
            {formatDate(data.period.start)} – {formatDate(data.period.end)}
          </span>
        )}
      </div>

      <SummaryBar summary={data?.summary} />

      {/* Filters */}
      <div style={{
        display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center',
        padding: 12, backgroundColor: '#f9fafb', borderRadius: 8, marginBottom: 14,
      }}>
        <input value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search property…" className="sales-input"
          style={{ fontSize: 13, flex: '1 1 200px', minWidth: 180 }} />
        <select value={regionFilter} onChange={e => setRegionFilter(e.target.value)}
          className="sales-input" style={{ fontSize: 13 }}>
          <option value="">All regions</option>
          {regionOptions.map(r => <option key={r} value={r}>{r}</option>)}
        </select>
        <select value={cityFilter} onChange={e => setCityFilter(e.target.value)}
          className="sales-input" style={{ fontSize: 13 }}>
          <option value="">All cities</option>
          {cityOptions.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
          className="sales-input" style={{ fontSize: 13 }}>
          <option value="">All statuses</option>
          <option value="MAXED_OUT">Maxed Out</option>
          <option value="UNDERPERFORMING">Underperforming</option>
          <option value="CRITICAL">Critical</option>
          <option value="INSUFFICIENT_DATA">Insufficient Data</option>
        </select>
        <button onClick={clearFilters} className="btn btn-sm"
          style={{ backgroundColor: '#fff', border: '1px solid #e5e7eb' }}>
          Clear
        </button>
        <span style={{ marginLeft: 'auto', fontSize: 12, color: '#6b7280' }}>
          Showing {filtered.length} of {data?.rows?.length || 0} properties
        </span>
      </div>

      {/* Report table */}
      <div className="table-wrapper" style={{ overflowX: 'auto' }}>
        <table className="data-table" style={{ fontSize: 12, minWidth: 1200 }}>
          <thead>
            <tr>
              <SortHeader k="property_name">Property</SortHeader>
              <SortHeader k="region">Region</SortHeader>
              <SortHeader k="city">City</SortHeader>
              <SortHeader k="final_grade" align="center">Grade</SortHeader>
              <SortHeader k="market_ceiling" align="center">Ceiling</SortHeader>
              <SortHeader k="status">Status</SortHeader>
              <SortHeader k="parking_snap_tows" align="right">Tows</SortHeader>
              <SortHeader k="tow_fee" align="right">Fee</SortHeader>
              <SortHeader k="revenue" align="right">Revenue</SortHeader>
              <SortHeader k="money_spent" align="right">Spent</SortHeader>
              <SortHeader k="profit" align="right">Profit</SortHeader>
              <SortHeader k="profit_margin" align="right">Margin</SortHeader>
              <SortHeader k="expense_ratio" align="right">Exp Ratio</SortHeader>
              <SortHeader k="no_charges" align="right">NCs</SortHeader>
              <SortHeader k="refunds" align="right">Refunds</SortHeader>
              <SortHeader k="true_no_charge_rate" align="right">True NC</SortHeader>
              <SortHeader k="courtesy_tows_attributed" align="right">CTs</SortHeader>
              <SortHeader k="primary_issue">Primary Issue</SortHeader>
            </tr>
          </thead>
          <tbody>
            {filtered.map(r => (
              <tr key={r.property_id}>
                <td>
                  <Link to={`/sales/properties/${r.property_id}`}
                    style={{ color: '#3F66A0', fontWeight: 600, textDecoration: 'none' }}>
                    {r.property_name}
                  </Link>
                </td>
                <td>{r.region || '—'}</td>
                <td>{r.city || '—'}</td>
                <td style={{ textAlign: 'center' }}><GradeChip grade={r.final_grade} /></td>
                <td style={{ textAlign: 'center', color: '#6b7280' }}>{r.market_ceiling || '—'}</td>
                <td><StatusChip status={r.status} /></td>
                <td style={{ textAlign: 'right' }}>{fmtCount(r.parking_snap_tows)}</td>
                <td style={{ textAlign: 'right' }}>{fmtMoney(r.tow_fee)}</td>
                <td style={{ textAlign: 'right' }}>{fmtMoney(r.revenue)}</td>
                <td style={{ textAlign: 'right' }}>{fmtMoney(r.money_spent)}</td>
                <td style={{ textAlign: 'right', color: r.profit < 0 ? '#dc2626' : undefined, fontWeight: 600 }}>
                  {fmtMoney(r.profit)}
                </td>
                <td style={{ textAlign: 'right' }}>{fmtPct(r.profit_margin)}</td>
                <td style={{ textAlign: 'right' }}>{fmtPct(r.expense_ratio)}</td>
                <td style={{ textAlign: 'right' }}>{fmtCount(r.no_charges)}</td>
                <td style={{ textAlign: 'right' }}>{fmtCount(r.refunds)}</td>
                <td style={{ textAlign: 'right' }}>{fmtPct(r.true_no_charge_rate)}</td>
                <td style={{ textAlign: 'right' }}>{fmtCount(r.courtesy_tows_attributed)}</td>
                <td style={{ fontSize: 11, color: '#6b7280' }}>
                  {r.primary_issue ? KPI_LABELS[r.primary_issue] : '—'}
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={18} style={{ textAlign: 'center', color: '#9ca3af', padding: 24 }}>
                  No properties match the current filters.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
